export type PhoneModelLike = {
  manufacturer: string
  model_name: string
  category?: string | null
}

export type PhoneModelSearchRow = PhoneModelLike & {
  id: number
  release_year?: number | null
}

const BASIC_BRANDS = new Set(['Doro', 'Emporia', 'CAT', 'Maxcom', 'Beafon'])

function normSearch(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/** Feature / button phones and similar devices without reliable AI pricing. */
export function isBasicPhoneModel(m: PhoneModelLike): boolean {
  const cat = (m.category || '').trim().toLowerCase()
  if (cat === 'basic_phone' || cat === 'feature_phone') return true
  if (cat === 'smartphone') return false
  if (BASIC_BRANDS.has(m.manufacturer)) return true
  const line = `${m.manufacturer} ${m.model_name}`.toLowerCase()
  if (/\b3310\b|\b2720\b|\b105\b|\b110\b|\bflip phone\b|\bfeature phone\b/.test(line)) return true
  if (m.manufacturer.toLowerCase() === 'nokia' && /\bnokia\s+\d{3,4}\b/.test(line)) return true
  return false
}

/** True when a catalog row belongs in the basic-phone sell flow (DB category only). */
export function isBasicPhoneCatalogModel(m: PhoneModelLike): boolean {
  const cat = (m.category || '').trim().toLowerCase()
  return cat === 'basic_phone' || cat === 'feature_phone'
}

/** Free-text search across the full phone model catalog (for basic / feature phones). */
export function searchPhoneModelsByQuery(
  models: PhoneModelSearchRow[],
  query: string,
  limit = 8,
): PhoneModelSearchRow[] {
  const q = query.trim().toLowerCase()
  if (q.length < 2) return []
  const words = q.split(/\s+/).filter(Boolean)
  const qNorm = normSearch(q)

  const scored: { m: PhoneModelSearchRow; score: number }[] = []

  for (const m of models) {
    const line = `${m.manufacturer} ${m.model_name}`.toLowerCase()
    const lineNorm = normSearch(line)
    let score = 0
    if (line === q || lineNorm === qNorm) score = 100
    else if (line.startsWith(q) || lineNorm.startsWith(qNorm)) score = 85
    else if (line.includes(q)) score = 70
    else if (words.length >= 2 && words.every((w) => line.includes(w))) score = 50
    else if (words.length === 1 && words[0].length >= 3 && line.includes(words[0])) score = 30
    else continue
    if (isBasicPhoneModel(m)) score += 8
    scored.push({ m, score })
  }

  scored.sort(
    (a, b) =>
      b.score - a.score ||
      a.m.manufacturer.localeCompare(b.m.manufacturer) ||
      a.m.model_name.localeCompare(b.m.model_name, undefined, { numeric: true }),
  )
  return scored.slice(0, limit).map((x) => x.m)
}

export function pickBestPhoneModelMatch(
  models: PhoneModelSearchRow[],
  query: string,
): PhoneModelSearchRow | null {
  const hits = searchPhoneModelsByQuery(models, query, 1)
  return hits[0] ?? null
}
